import React from "react";
import classnames from "classnames";
import { DropzoneOptions, useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

import { Button, Banner } from "..";
import { FileInterface, Progress } from "../../types/common";
import { UploadIcon } from "../Icons";
import FileList from "../FileList";

import styles from "./FileUploader.module.scss";

interface Props extends DropzoneOptions {
  id: string;
  errors: string[];
  bannerTitle: string;
  isErrorDismiss: boolean;
  files: FileInterface[];
  uploadProgressData: Progress;
  hintText?: string;
  onRemove: (id: string) => void;
  onDownload?: (id: string, fileName: string) => void;
  onErrorDismiss: () => void;
  isViewOnly?: boolean;
  isUploadHidden?: boolean;
  isReadonlyMode?: boolean;
  isDownloadActive?: boolean;
  isFileListOnTop?: boolean;
  onDescriptionBlur?: (id: string, description: string) => void;
  onDescriptionChanged?: (id: string, description: string) => void;
}

const defaultFn = () => {};

const FileUploader = (props: Props) => {
  const {
    id,
    files,
    onRemove,
    onDownload = defaultFn,
    hintText,
    errors,
    bannerTitle,
    disabled,
    isErrorDismiss,
    onErrorDismiss,
    uploadProgressData,
    onDescriptionBlur,
    onDescriptionChanged,
    isViewOnly,
    isReadonlyMode,
    isDownloadActive,
    isUploadHidden,
    isFileListOnTop,
    ...dropzoneProps
  } = props;

  const { t } = useTranslation();

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    ...dropzoneProps,
    noClick: true,
    disabled: disabled || isReadonlyMode,
  });

  const onDismiss = isErrorDismiss ? onErrorDismiss : undefined;

  return (
    <div
      className={classnames(
        styles.fileUploader,
        isFileListOnTop && styles.fileUploaderListOnTop
      )}
    >
      {!isReadonlyMode && !isUploadHidden && (
        <div
          className={classnames({
            [styles.dropzoneBlock]: true,
            [styles.dropzoneActive]: isDragActive,
            [styles.dropzoneDisabled]: disabled,
          })}
          {...getRootProps()}
        >
          <input {...getInputProps()} id={id} />
          {!disabled && (
            <div className={styles.dropzoneBlockText}>
              <Button
                id="open-file-dialog-button"
                variant="outlined"
                className={styles.dropzoneUploadButton}
                onClick={open}
                iconPosition="left"
                iconComponent={UploadIcon}
                disabled={isReadonlyMode}
              >
                {t("uploader.uploadButton")}
              </Button>
              <span>{t("uploader.dragDropText")}</span>
            </div>
          )}
          {hintText && (
            <span className={styles.dropzoneHintText}>{hintText}</span>
          )}
        </div>
      )}
      {!!errors.length && (
        <Banner
          variant="error"
          id="files-errors-banner"
          title={t(bannerTitle)}
          onDismiss={onDismiss}
        >
          {errors.map((errorText) => (
            <span className={styles.dropzoneErrorText} key={errorText}>
              {errorText}
            </span>
          ))}
        </Banner>
      )}
      <FileList
        files={files}
        onRemove={onRemove}
        onDownload={onDownload}
        disabled={isReadonlyMode}
        isViewOnly={isViewOnly}
        isDownloadActive={isDownloadActive}
        uploadProgressData={uploadProgressData}
        onDescriptionBlur={onDescriptionBlur}
        onDescriptionChanged={onDescriptionChanged}
      />
    </div>
  );
};

export default FileUploader;
